import React from "react";
import {
  Activity,
  BookOpen,
  Bot,
  Braces,
  Check,
  ChevronRight,
  CircleAlert,
  Download,
  ExternalLink,
  FileJson,
  Gauge,
  GitCompareArrows,
  History,
  KeyRound,
  Network,
  ShieldCheck,
  TableProperties,
  Tags,
  UploadCloud,
} from "lucide-react";
import { CardHeader, PageHeading, StatusChip } from "./AuditUI";

const QUICK_STEPS = [
  {
    number: "01",
    title: "Export from GTM",
    description: "Download the required workspace or published version as a JSON container file.",
    icon: Download,
  },
  {
    number: "02",
    title: "Load the JSON",
    description: "Upload, drag and drop, or paste the export into the Overview page.",
    icon: UploadCloud,
  },
  {
    number: "03",
    title: "Review the audit",
    description: "Check deterministic scores, priority findings, consent, and dependency risks.",
    icon: Activity,
  },
  {
    number: "04",
    title: "Confirm naming",
    description: "Review the inferred prefix, separator, and casing rules before applying them.",
    icon: Tags,
  },
  {
    number: "05",
    title: "Add AI if needed",
    description: "Optionally connect a session-only provider key for a second-opinion review.",
    icon: Bot,
  },
  {
    number: "06",
    title: "Export the report",
    description: "Download a Word report, findings CSV, or structured audit JSON.",
    icon: FileJson,
  },
];

const GTM_EXPORT_STEPS = [
  ["Open the container", "Sign in to Google Tag Manager and open the container you want to audit."],
  ["Open Admin", "Choose Admin from the top navigation."],
  ["Choose Export Container", "In the Container column, select Export Container."],
  ["Select the source", "Choose a version or workspace, then select the exact state you want to audit."],
  ["Download JSON", "Click Download and keep the exported .json file unchanged for the audit."],
];

const WORKSPACE_GUIDE = [
  {
    id: "overview",
    label: "Overview",
    icon: Activity,
    description: "Import the container and see overall, health, naming, and measurement ratings.",
    action: "Import container",
  },
  {
    id: "naming",
    label: "Naming",
    icon: Tags,
    description: "Inspect inferred conventions and identify tags, triggers, or variables that break them.",
    action: "Review naming",
  },
  {
    id: "events",
    label: "Events",
    icon: Network,
    description: "Check GA4 events and optionally validate sample dataLayer payloads.",
    action: "Check events",
  },
  {
    id: "governance",
    label: "Governance",
    icon: Gauge,
    description: "Apply policy gates for consent, paused tags, naming, Custom HTML, and GA4 coverage.",
    action: "Open controls",
  },
  {
    id: "compare",
    label: "Compare",
    icon: GitCompareArrows,
    description: "Load a baseline export and inspect added, removed, renamed, or changed objects.",
    action: "Compare versions",
  },
  {
    id: "explorer",
    label: "Explorer",
    icon: TableProperties,
    description: "Search and filter the complete inventory of tags, triggers, and variables.",
    action: "Browse objects",
  },
  {
    id: "ai",
    label: "AI Review",
    icon: Bot,
    description: "Use OpenRouter, OpenAI, Gemini, or Claude for optional structured recommendations.",
    action: "Configure AI",
  },
  {
    id: "history",
    label: "History",
    icon: History,
    description: "Save sanitized audit snapshots when the optional Cloudflare D1 binding is enabled.",
    action: "View history",
  },
  {
    id: "report",
    label: "Report",
    icon: FileJson,
    description: "Review all findings and export Word, CSV, or JSON deliverables.",
    action: "Export report",
  },
];

const TROUBLESHOOTING = [
  {
    title: "The JSON is rejected",
    answer: "Export the file directly from GTM. Do not upload a GA4 report, browser network log, or unrelated JSON file.",
  },
  {
    title: "AI says Worker required",
    answer: "Run the frontend and Worker together with npm run dev:full, or configure VITE_API_BASE_URL for your deployed Worker.",
  },
  {
    title: "No AI models appear",
    answer: "Validate the selected provider key, refresh the model catalog, and confirm the key has access to text-generation models.",
  },
  {
    title: "History is unavailable",
    answer: "History is optional. Add the D1 database binding and migration; the rest of the audit still works without it.",
  },
];

export function GuidePage({ switchTab, backendStatus, loadDemo }) {
  return (
    <section className="page guide-page">
      <PageHeading
        eyebrow="Guide / First audit"
        title="From GTM export to an actionable report"
        description="Follow the complete workflow, understand every workspace, and keep raw container data inside your browser."
        actions={
          <div className="page-action-stack">
            <StatusChip tone="success" icon={ShieldCheck}>Local deterministic audit</StatusChip>
            <StatusChip tone={backendStatus === "online" ? "success" : "warning"}>
              Worker {backendStatus}
            </StatusChip>
          </div>
        }
      />

      <article className="card guide-hero">
        <div>
          <span className="guide-hero-icon"><BookOpen aria-hidden="true" /></span>
          <p className="eyebrow">Six-step workflow</p>
          <h2>Audit confidently without touching the live GTM container</h2>
          <p>
            GTM Audit Atlas reads an exported copy. Importing a file here cannot publish,
            overwrite, or change anything in Google Tag Manager.
          </p>
          <div className="button-row">
            <button className="primary-button" type="button" onClick={() => switchTab("overview")} title="Open the container import area">
              <UploadCloud aria-hidden="true" /> Start an audit
            </button>
            <button className="secondary-button" type="button" onClick={loadDemo} title="Load the included safe demonstration container">
              <Braces aria-hidden="true" /> Try the demo first
            </button>
          </div>
        </div>
        <div className="guide-privacy-seal">
          <ShieldCheck aria-hidden="true" />
          <strong>Raw JSON stays local</strong>
          <span>AI receives only bounded, sanitized audit metadata when you explicitly request it.</span>
        </div>
      </article>

      <div className="guide-step-grid">
        {QUICK_STEPS.map(({ number, title, description, icon: Icon }) => (
          <article className="card guide-step" key={number}>
            <span>{number}</span>
            <Icon aria-hidden="true" />
            <h3>{title}</h3>
            <p>{description}</p>
          </article>
        ))}
      </div>

      <div className="guide-main-grid">
        <article className="card gtm-export-guide">
          <CardHeader
            step="A"
            title="How to export a GTM container"
            description="Use a workspace for the latest draft state or a saved version for a stable historical snapshot."
            action={
              <a
                className="secondary-button compact-link"
                href="https://support.google.com/tagmanager/answer/6106997?hl=en"
                target="_blank"
                rel="noreferrer"
              >
                Google guide <ExternalLink aria-hidden="true" />
              </a>
            }
          />
          <ol className="gtm-export-steps">
            {GTM_EXPORT_STEPS.map(([title, description], index) => (
              <li key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div><strong>{title}</strong><p>{description}</p></div>
                <Check aria-hidden="true" />
              </li>
            ))}
          </ol>
          <div className="guide-warning">
            <CircleAlert aria-hidden="true" />
            <div>
              <strong>Prefer a complete export</strong>
              <p>
                A partial GTM export can omit dependencies. Missing tag and trigger references
                may be removed by GTM, while variable references can remain, which can make the
                audit less representative.
              </p>
            </div>
          </div>
        </article>

        <aside className="card guide-file-check">
          <CardHeader
            step="B"
            title="Before uploading"
            description="A quick file-quality check prevents misleading results."
          />
          <ul>
            <li><Check aria-hidden="true" /><span><strong>File type</strong> A `.json` file downloaded from GTM</span></li>
            <li><Check aria-hidden="true" /><span><strong>Correct source</strong> The intended account, container, and workspace/version</span></li>
            <li><Check aria-hidden="true" /><span><strong>Complete scope</strong> Include dependencies whenever possible</span></li>
            <li><Check aria-hidden="true" /><span><strong>Safe copy</strong> Keep the original export unchanged</span></li>
          </ul>
          <div className="guide-file-visual">
            <FileJson aria-hidden="true" />
            <span><strong>container-export.json</strong><small>Ready for local parsing</small></span>
          </div>
        </aside>
      </div>

      <article className="card workspace-guide-card">
        <CardHeader
          step="C"
          title="What every workspace does"
          description="Hover a card for emphasis or open the workspace directly."
        />
        <div className="workspace-guide-grid">
          {WORKSPACE_GUIDE.map(({ id, label, icon: Icon, description, action }) => (
            <button
              className="workspace-guide-item"
              type="button"
              key={id}
              onClick={() => switchTab(id)}
              title={`${action}: ${description}`}
            >
              <span><Icon aria-hidden="true" /></span>
              <strong>{label}</strong>
              <p>{description}</p>
              <small>{action}<ChevronRight aria-hidden="true" /></small>
            </button>
          ))}
        </div>
      </article>

      <div className="guide-main-grid">
        <article className="card guide-export-report">
          <CardHeader
            step="D"
            title="How to export audit results"
            description="Run an audit first, then use the Report page or the header export button."
          />
          <div className="report-format-grid">
            <div><span>DOCX</span><strong>Client-ready report</strong><p>Ratings, governance, findings, comparison, and optional AI review.</p></div>
            <div><span>CSV</span><strong>Finding register</strong><p>Filter, assign, or analyze every issue in a spreadsheet.</p></div>
            <div><span>JSON</span><strong>Structured audit data</strong><p>Machine-readable results for automation or version control.</p></div>
          </div>
          <button className="primary-button" type="button" onClick={() => switchTab("report")} title="Open the report and export workspace">
            <Download aria-hidden="true" /> Open report exports
          </button>
        </article>

        <aside className="card guide-ai-note">
          <CardHeader
            step="E"
            title="Optional AI setup"
            description="The deterministic audit does not require an API key."
          />
          <div className="guide-ai-flow">
            <KeyRound aria-hidden="true" />
            <div><strong>Choose a provider</strong><span>OpenRouter, OpenAI, Gemini, or Claude</span></div>
            <ChevronRight aria-hidden="true" />
            <div><strong>Validate the key</strong><span>Load the live compatible model catalog</span></div>
            <ChevronRight aria-hidden="true" />
            <div><strong>Give consent</strong><span>Send sanitized metadata only when requested</span></div>
          </div>
          <button className="secondary-button" type="button" onClick={() => switchTab("ai")} title="Open provider and model settings">
            <Bot aria-hidden="true" /> Configure AI review
          </button>
        </aside>
      </div>

      <article className="card guide-troubleshooting">
        <CardHeader
          step="F"
          title="Common problems"
          description="Fast checks for the most likely setup issues."
        />
        <div>
          {TROUBLESHOOTING.map((item) => (
            <details key={item.title}>
              <summary>{item.title}<ChevronRight aria-hidden="true" /></summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </article>
    </section>
  );
}
